import React, { useState } from 'react';
import { Box, Container, Typography } from '@mui/material';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
import ComplianceDetailsDrawer from '../components/ComplianceDetailsDrawer';
import ComplianceTable, { ComplianceTransaction } from '../components/ComplianceTable';
import { useToast } from '../components/ui/ToastProvider';
// Mock data
const MOCK_COMPLIANCE_TRANSACTIONS: ComplianceTransaction[] = Array(25).fill(null).map((_, index) => {
  const payoutTypes: ('FT' | 'IBFT' | 'OTC')[] = ['FT', 'IBFT', 'OTC'];
  const payoutType = payoutTypes[index % 3];
  const baseTime = Date.now() - index * 3600000;
  const agentCodes = ['Bank of AJ&K', 'BIPL', 'FBL', 'HBL', 'UBL', 'MCB', 'ABL'];
  const branchCodes = ['0001', '0002', '0003', '0004', '0005', '0006', '0007', '0008'];
  return {
    txnId: `TXN-${15000 + index}`,
    mtoName: ['RIA Money Transfer', 'MoneyGram', 'Western Union', 'Xpress Money'][index % 4],
    payoutType,
    beneficiaryName: `Beneficiary ${index + 1}`,
    beneficiaryCnic: `${String(35202 + index).padStart(5, '0')}-${String(1234567 + index).padStart(7, '0')}-${index % 10}`,
    beneficiaryTitle: `${String(index).padStart(16, '0')}`,
    amountFcy: `${(500 + index * 25).toFixed(2)}`,
    currencyFcy: 'USD',
    amountPkr: `${((500 + index * 25) * 279).toLocaleString()}`,
    createdAt: new Date(baseTime - 5000).toISOString(),
    lastUpdateAt: new Date(baseTime).toISOString(),
    lifecycle: 'IN_REVIEW',
    currentState: 'SCREENING',
    mtoRef: `MTO-${25000 + index}`,
    bankRef: `BNK-${35000 + index}`,
    branchCode: payoutType === 'OTC' ? branchCodes[index % branchCodes.length] : '',
    agentCode: payoutType === 'OTC' ? agentCodes[index % agentCodes.length] : '',
    railRefs: {},
    screening: {
      engine: 'WorldCheck',
      score: 75 + index % 20,
      hits: ['Potential PEP match', 'Name similarity with sanctioned entity', 'High-risk jurisdiction'].slice(0, index % 3 + 1),
      failReasonCode: 'MANUAL_REVIEW_REQUIRED',
      failReasonText: 'Transaction flagged for manual review due to screening alerts',
      screenedAt: new Date(baseTime - 2000).toISOString()
    },
    remitterName: `Remitter ${index + 1}`,
    remitterIdType: 'Passport',
    remitterIdNumber: `P${String(1000000 + index).padStart(7, '0')}`,
    remitterCountry: ['UAE', 'Saudi Arabia', 'UK', 'USA'][index % 4],
    beneficiaryIban: payoutType === 'IBFT' ? `PK${String(index).padStart(22, '0')}` : undefined,
    beneficiaryBank: payoutType === 'IBFT' ? ['HBL', 'UBL', 'MCB', 'ABL'][index % 4] : undefined,
    purposeCode: ['FAM', 'SAL', 'EDU', 'MED'][index % 4],
    purposeText: ['Family Support', 'Salary', 'Education', 'Medical'][index % 4],
    fxRate: 279 + index % 5 * 0.5,
    amendAllowed: true,
    cancelAllowed: true,
    amendStatus: 'NONE',
    cancelStatus: 'NONE'
  } as any;
});
interface ComplianceReviewProps {
  onLogout: () => void;
}
const ComplianceReview: React.FC<ComplianceReviewProps> = ({
  onLogout
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [transactions, setTransactions] = useState<ComplianceTransaction[]>(MOCK_COMPLIANCE_TRANSACTIONS);
  const [selectedTransaction, setSelectedTransaction] = useState<ComplianceTransaction | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const {
    showToast
  } = useToast();
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const handleRowClick = (transaction: ComplianceTransaction) => {
    setSelectedTransaction(transaction);
    setDrawerOpen(true);
  };
  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedTransaction(null);
  };
  const handleTransactionRemoval = (txnId: string) => {
    setTransactions(prev => prev.filter(txn => txn.txnId !== txnId));
    handleCloseDrawer();
  };
  const handleExport = () => {
    showToast('CSV export initiated', 'success');
  };
  return <Box sx={{
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: '#F8FAFC'
  }} data-id="element-3372" data-mp="ps30a4i8er3w">
      <TopBar onMenuClick={handleDrawerToggle} onLogout={onLogout} data-id="element-3373" data-mp="3p5wx1ja5ykeb" />
      <Sidebar open={mobileOpen} onClose={handleDrawerToggle} activePage="compliance" data-id="element-3374" data-mp="3t9uwxw7bbz8f" />
      <Box component="main" sx={{
      flexGrow: 1,
      p: 3,
      width: {
        sm: `calc(100% - 240px)`
      },
      ml: 0,
      mt: 8
    }} data-id="element-3375" data-mp="20xkfpcajytl">
        <Container maxWidth="xl" data-id="element-3376" data-mp="35hwij3ckhnr2">
          <Box sx={{
          mb: 3
        }} data-id="element-3377" data-mp="3ai24q402ii1r">
            <Typography variant="h4" fontWeight={600} gutterBottom data-id="element-3378" data-mp="32z2kjw4s8mhc">
              Compliance Review / Screening
            </Typography>
            <Typography variant="body1" color="text.secondary" data-id="element-3379" data-mp="2y1j9t67amru5">
              Review and approve transactions flagged for manual screening
            </Typography>
          </Box>
          <ComplianceTable transactions={transactions} onRowClick={handleRowClick} onExport={handleExport} data-id="element-3380" data-mp="2p0bbwp8ovt0l" />
        </Container>
      </Box>
      {selectedTransaction && <ComplianceDetailsDrawer open={drawerOpen} onClose={handleCloseDrawer} transaction={selectedTransaction} onTransactionRemoval={handleTransactionRemoval} data-id="element-3381" data-mp="2f8yjnwizb0u1" />}
    </Box>;
};
export default ComplianceReview;